// X (Twitter) Ads Pixel — Universal Website Tag (twq / uwt.js).
//
// Mirrors frontend/src/api/gtag.ts: constants live in-repo (not VITE_), the
// script is deferred via window.__nvDeferTag so it does not contend with first
// paint, and Purchase is locally deduped on transaction_id.
//
// Event IDs come from X Ads Events Manager (created for the TOKEN2049 flight).
// X has no InitiateCheckout event type on this pixel — AddToCart is mapped to
// our checkout_started moment (same place gtagConversion('checkout_started')
// fires). These IDs are public (they ship in page source), same as AW-….

/** Pixel / Event Source ID from Events Manager. */
export const X_PIXEL_ID = 'rf6a6'

/** Per-event IDs from Events Manager (tw-<pixel>-…). */
export const X_EVENT_IDS = {
  pageView: 'tw-rf6a6-rf6ad',
  /** Mapped to checkout start — X Events Manager has no InitiateCheckout here. */
  addToCart: 'tw-rf6a6-rf6ah',
  purchase: 'tw-rf6a6-rf6ak',
} as const

type TwqFn = (...args: unknown[]) => void
declare global {
  interface Window {
    twq?: TwqFn & { exe?: (...args: unknown[]) => void; queue?: unknown[]; version?: string }
  }
}

let initialised = false

/** Inject the UWT stub + config once, at app start. Called from main.tsx.
 *
 *  Loading at startup (deferred until after paint) lets the tag read twclid
 *  off the landing URL before SPA navigation strips the query. */
export function initXPixel(): void {
  if (initialised || !X_PIXEL_ID || typeof window === 'undefined') return
  initialised = true

  // Official stub: queues calls until uwt.js lands.
  if (!window.twq) {
    // Official UWT stub: once uwt.js lands it sets twq.exe; later calls must
    // dispatch through exe or they queue forever and AddToCart/Purchase die.
    const twq = function twq(this: unknown) {
      // eslint-disable-next-line prefer-rest-params
      const self = twq as TwqFn & {
        exe?: (...args: unknown[]) => void
        queue: unknown[]
        version: string
      }
      if (self.exe) self.exe.apply(self, arguments as unknown as unknown[])
      else self.queue.push(arguments)
    } as TwqFn & { exe?: (...args: unknown[]) => void; queue: unknown[]; version: string }
    twq.queue = []
    twq.version = '1.1'
    window.twq = twq
  }

  window.twq('config', X_PIXEL_ID)

  const load = () => {
    if (document.querySelector('script[data-nv-xpixel]')) return
    const s = document.createElement('script')
    s.async = true
    s.src = 'https://static.ads-twitter.com/uwt.js'
    s.dataset.nvXpixel = '1'
    document.head.appendChild(s)
    // Base PageView once the config is queued (replays when uwt.js arrives).
    window.twq?.('event', X_EVENT_IDS.pageView, {})
  }
  const defer = (window as unknown as { __nvDeferTag?: (fn: () => void) => void }).__nvDeferTag
  if (defer) defer(load)
  else load()
}

const SENT_KEY = 'nimvoy.xads.sent'
const SENT_CAP = 25

function alreadySent(txnId: string): boolean {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    return raw ? (JSON.parse(raw) as string[]).includes(txnId) : false
  } catch {
    return false
  }
}

function markSent(txnId: string): void {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    const list = raw ? (JSON.parse(raw) as string[]) : []
    if (!list.includes(txnId)) list.push(txnId)
    localStorage.setItem(SENT_KEY, JSON.stringify(list.slice(-SENT_CAP)))
  } catch {
    /* X's conversion_id / our local guard is best-effort */
  }
}

/** Fire AddToCart at checkout start (our InitiateCheckout moment). */
export function xPixelAddToCart(opts?: { valueCents?: number; currency?: string; contentId?: string }): void {
  if (!X_PIXEL_ID || !window.twq) return
  const payload: Record<string, unknown> = {}
  if (opts?.valueCents != null) payload.value = opts.valueCents / 100
  if (opts?.currency) payload.currency = opts.currency.toUpperCase()
  if (opts?.contentId) {
    payload.content_ids = [opts.contentId]
    payload.content_type = 'product'
  }
  window.twq('event', X_EVENT_IDS.addToCart, payload)
}

/** Fire Purchase. Safe to call twice for the same order (payment + delivered);
 *  localStorage dedupes on transaction_id the same way gtagPurchase does. */
export function xPixelPurchase(opts: {
  transactionId: string
  valueCents: number
  currency: string
}): void {
  if (!X_PIXEL_ID || !window.twq) return
  if (!opts.transactionId || alreadySent(opts.transactionId)) return
  markSent(opts.transactionId)
  window.twq('event', X_EVENT_IDS.purchase, {
    value: opts.valueCents / 100,
    currency: (opts.currency || 'usd').toUpperCase(),
    conversion_id: opts.transactionId,
  })
}